import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '@nanostores/react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { isToday, isYesterday, isThisWeek, subDays } from 'date-fns';
import { classNames } from '~/utils/classNames';
import { $activeGeneration } from '~/lib/stores/activeGeneration';
import { $isAuthenticated, $currentUser, clearAuth } from '~/lib/stores/auth';
import { $showAuthModal } from '~/lib/stores/authModal';
import { $mobileSidebarOpen, closeMobileSidebar } from '~/lib/stores/sidebar';
import useViewport from '~/lib/hooks/useViewport';
import { TOUCH_QUERY } from '~/lib/hooks/useMediaQuery';
import { HandedOffSection } from './HandedOffSection';
import { $liveCredits } from '~/lib/stores/credits';
import { openAccountSettings } from '~/lib/stores/accountSettings';
import { chatStore } from '~/lib/stores/chat';
import { workbenchStore } from '~/lib/stores/workbench';
import { getAll, deleteById } from '~/lib/persistence/db';
import type { ChatHistoryItem } from '~/lib/persistence/useChatHistory';
import { description as descriptionAtom } from '~/lib/persistence/useChatHistory';
import { overlayLiveTitle } from './live-title';
import { useEditChatDescription } from '~/lib/hooks';
import { apiLogout } from '~/lib/api/auth';
import { cancelGeneration } from '~/lib/api/chats';
import { apiGetSubscription, type BillingSubscription } from '~/lib/api/billing';
import { capture } from '~/lib/posthog';
import { ANALYTICS_EVENTS } from '@dunea/shared';
import { AccessibleModal } from '~/components/account/AccessibleModal';
import { formatCredits } from '~/lib/plans-model';

/*
 * Sidebar — the workspace navigator.
 *
 * Styling follows DESIGN.md: UnoCSS utility classes + `ui-*` shortcuts only —
 * no raw CSS vars or hex, no gradients/glows, no rounded-container-with-left-
 * accent trope. Active nav uses the endorsed `bg-sunken` fill (ui-tab-active).
 */

const COLLAPSED_KEY = 'dunea-sidebar-collapsed';
const SECTIONS_KEY = 'dunea-sidebar-collapsed-sections';
const EXPANDED_WIDTH = 258;
const COLLAPSED_WIDTH = 56;

/*
 * Below this width the sidebar is an off-canvas drawer instead of a column.
 * Matches Uno's `lg`. At 258px fixed, the old always-in-flow sidebar ate two
 * thirds of a 390px phone and pushed the composer off-screen entirely.
 */
const DRAWER_BREAKPOINT = 1024;

// UI motion: calm, ease-out, editorial (DESIGN.md — subtle, purposeful).
const FAST = { duration: 0.2, ease: 'easeOut' } as const;

/* ─────────────────────────── date bucketing ─────────────────────────── */

type Section = { key: string; label: string; items: ChatHistoryItem[] };

const SECTION_ORDER = ['today', 'yesterday', 'thisWeek', 'lastWeek', 'last30', 'older'] as const;
const SECTION_LABEL: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This week',
  lastWeek: 'Last week',
  last30: 'Last 30 days',
  older: 'Older',
};

function bucketKey(date: Date, now: Date): string {
  if (isToday(date)) {
    return 'today';
  }

  if (isYesterday(date)) {
    return 'yesterday';
  }

  if (isThisWeek(date)) {
    return 'thisWeek';
  }

  if (date >= subDays(now, 14)) {
    return 'lastWeek';
  }

  if (date >= subDays(now, 30)) {
    return 'last30';
  }

  return 'older';
}

function bucketChats(chats: ChatHistoryItem[]): Section[] {
  const now = new Date();
  const map = new Map<string, ChatHistoryItem[]>();

  for (const item of chats) {
    const key = bucketKey(new Date(item.timestamp), now);
    const arr = map.get(key) ?? [];
    arr.push(item);
    map.set(key, arr);
  }

  return SECTION_ORDER.filter((k) => map.has(k)).map((k) => ({
    key: k,
    label: SECTION_LABEL[k],
    items: (map.get(k) ?? []).sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? '')),
  }));
}

function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function loadCollapsedSections(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(SECTIONS_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

/* ─────────────────────────── project row ─────────────────────────── */

function ProjectRow({
  item,
  isActive,
  deletingId,
  onOpen,
  onDelete,
}: {
  item: ChatHistoryItem;
  isActive: boolean;
  deletingId: string | null;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const { editing, saving, handleChange, handleBlur, handleSubmit, currentDescription, toggleEditMode } =
    useEditChatDescription({
      initialDescription: item.description ?? '',
      customChatId: item.id,
      syncWithGlobalStore: isActive,
    });

  const activeGen = useStore($activeGeneration);
  const isGenerating =
    !!activeGen &&
    (activeGen.id === item.id ||
      activeGen.urlId === item.id ||
      (!!item.urlId && (activeGen.id === item.urlId || activeGen.urlId === item.urlId)));

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const dotsRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;

      if (!dotsRef.current?.contains(t) && !document.getElementById(`row-menu-${item.id}`)?.contains(t)) {
        setMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', onDocClick);

    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen, item.id]);

  function openMenu() {
    const rect = dotsRef.current?.getBoundingClientRect();

    if (rect) {
      // Flip above the row when the menu would run off the bottom of the screen, and keep it inside the viewport.
      const menuHeight = window.matchMedia(TOUCH_QUERY).matches ? 96 : 72;
      const below = rect.bottom + 4;
      const top = below + menuHeight > window.innerHeight - 8 ? Math.max(8, rect.top - 4 - menuHeight) : below;
      setMenuPos({ top, left: Math.min(Math.max(8, rect.right - 156), window.innerWidth - 164) });
      setMenuOpen(true);
    }
  }

  return (
    <div
      className={classNames(
        'group relative flex h-10 items-center gap-2.5 rounded-lg border pl-3.5 pr-2 outline-none transition-[background-color,border-color,color] duration-200 ease-out',
        // Selected = sunken well + soft border + accent marker (pressed-in). Hover = raised fill only. Tokens only.
        isActive
          ? 'border-subtle bg-sunken text-primary'
          : 'border-transparent text-secondary hover:border-hairline hover:bg-raised hover:text-primary',
      )}
      role="button"
      tabIndex={0}
      onClick={() => !editing && onOpen()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !editing) {
          onOpen();
        }
      }}
    >
      {/* Generation indicator — spinner (stop on hover) only while generating; idle rows stay text-first. */}
      {isGenerating && (
        <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center touch:h-8 touch:w-8">
          <span
            className="i-svg-spinners:90-ring-with-bg text-[13px] text-accent group-hover:hidden touch:hidden"
            title="Generating…"
          />
          {/* Touch has no hover, so the stop control is always shown there. */}
          <button
            type="button"
            title="Stop generation"
            aria-label="Stop generation"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              cancelGeneration(item.id).catch(() => {});
              $activeGeneration.set(null);
            }}
            className="hidden cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-danger group-hover:inline-flex touch:inline-flex touch:h-8 touch:w-8"
          >
            <span className="i-ph:stop-fill text-[12px]" />
          </button>
        </span>
      )}

      {editing ? (
        <form
          onSubmit={handleSubmit}
          className="flex min-w-0 flex-1 items-center gap-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            autoFocus
            value={currentDescription}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.currentTarget.blur();
              }
            }}
            disabled={saving}
            aria-label="Project name"
            className="min-w-0 flex-1 rounded border border-accent bg-sunken px-1.5 py-0.5 text-[13px] text-primary outline-none"
          />
          <button
            type="submit"
            aria-label="Save name"
            title="Save"
            disabled={saving}
            onMouseDown={(e) => {
              if (!saving) {
                handleSubmit(e);
              }
            }}
            className={classNames(
              'h-4 w-4 flex-shrink-0 transition-colors',
              saving ? 'i-svg-spinners:90-ring-with-bg text-muted' : 'i-ph:check text-muted hover:text-accent',
            )}
          />
        </form>
      ) : (
        <>
          <span
            className={classNames(
              'flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] tracking-[-0.01em] transition-colors duration-200',
              isActive ? 'font-medium text-primary' : 'text-secondary group-hover:text-primary',
            )}
          >
            {currentDescription || 'Untitled'}
          </span>

          <button
            ref={dotsRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();

              if (menuOpen) {
                setMenuOpen(false);
              } else {
                openMenu();
              }
            }}
            className={classNames(
              'flex h-5 w-5 flex-shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted transition-[background-color,color,opacity] duration-200 ease-out hover:bg-elevated hover:text-secondary focus-visible:opacity-100 touch:h-8 touch:w-8',
              menuOpen || isActive
                ? 'opacity-100'
                : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 touch:opacity-100',
            )}
            title="More"
            aria-label="Project actions"
          >
            <span className="i-ph:dots-three text-[14px]" />
          </button>
        </>
      )}

      {createPortal(
        <AnimatePresence>
          {menuOpen && menuPos && (
            <motion.div
              id={`row-menu-${item.id}`}
              className="ui-root fixed z-[9999] w-[156px] overflow-hidden rounded-[10px] border border-subtle bg-surface p-1 shadow-[0_16px_40px_-18px_rgba(0,0,0,0.7)]"
              initial={{ opacity: 0, y: -2 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={FAST}
              style={{ top: menuPos.top, left: menuPos.left }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  toggleEditMode();
                }}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-md border-0 bg-transparent px-2.5 py-1.5 text-left text-[12.5px] text-secondary transition-colors duration-200 hover:bg-raised touch:py-2.5"
              >
                <span className="i-ph:pencil-simple text-[14px] opacity-70" />
                Rename
              </button>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onDelete();
                }}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-md border-0 bg-transparent px-2.5 py-1.5 text-left text-[12.5px] text-danger transition-colors duration-200 hover:bg-danger/10 touch:py-2.5"
              >
                <span
                  className={classNames(
                    'text-[14px] opacity-80',
                    deletingId === item.id ? 'i-svg-spinners:90-ring-with-bg' : 'i-ph:trash',
                  )}
                />
                Delete
              </button>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
}

/* ─────────────────────────── collapsible section ─────────────────────────── */

function SectionGroup({
  section,
  collapsed,
  onToggle,
  reduceMotion,
  children,
}: {
  section: Section;
  collapsed: boolean;
  onToggle: () => void;
  reduceMotion: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-7 first:mt-1">
      <button
        type="button"
        onClick={onToggle}
        className="group mb-2.5 flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-0.5 text-left"
      >
        <span className="ui-caption transition-colors duration-200 group-hover:text-secondary">{section.label}</span>
        <motion.span
          className="i-ph:caret-down text-[9px] text-faint transition-colors duration-200 group-hover:text-secondary"
          animate={{ rotate: collapsed ? -90 : 0 }}
          transition={reduceMotion ? { duration: 0 } : FAST}
        />
      </button>

      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={reduceMotion ? { opacity: 1 } : { height: 0, opacity: 0 }}
            animate={reduceMotion ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={reduceMotion ? { duration: 0 } : FAST}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─────────────────────────── collapsed rail button ─────────────────────────── */

function RailButton({
  icon,
  title,
  onClick,
  accent,
}: {
  icon: string;
  title: string;
  onClick: () => void;
  accent?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={classNames(
        'flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent transition-colors duration-200 hover:bg-raised hover:text-primary touch:h-10 touch:w-10',
        accent ? 'text-accent' : 'text-secondary',
      )}
    >
      <span className={classNames(icon, 'text-[16px]')} />
    </button>
  );
}

/* ─────────────────────────── main sidebar ─────────────────────────── */

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = !!useReducedMotion();
  const isAuthenticated = useStore($isAuthenticated);
  const user = useStore($currentUser);
  const chat = useStore(chatStore);
  const liveCredits = useStore($liveCredits);
  const [chats, setChats] = useState<ChatHistoryItem[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [chatToDelete, setChatToDelete] = useState<ChatHistoryItem | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [collapsedPref, setCollapsed] = useState(loadCollapsed);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(loadCollapsedSections);
  const [subscription, setSubscription] = useState<BillingSubscription | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const [search, setSearch] = useState('');
  const profileRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const isOnChat = location.pathname.startsWith('/chat/');
  const activeChatId = isOnChat ? location.pathname.split('/chat/')[1] : null;

  // Drawer mode: the sidebar leaves the flex flow and slides over the content.
  const isDrawer = useViewport(DRAWER_BREAKPOINT);
  const drawerOpen = useStore($mobileSidebarOpen);

  // The rail (icon-only) collapse is a desktop affordance. In the drawer the
  // sidebar is already hidden by default, so a second hidden state is noise.
  const collapsed = isDrawer ? false : collapsedPref;

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;

      try {
        localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // ignore
      }

      return next;
    });
  }

  function toggleSection(key: string) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      try {
        localStorage.setItem(SECTIONS_KEY, JSON.stringify([...next]));
      } catch {
        // ignore
      }

      return next;
    });
  }

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    getAll()
      .then(setChats)
      .catch(() => {});
  }, [isAuthenticated, chat.activeChat]);

  // The AI-generated title streams in mid-turn (Chat.client sets the global
  // description atom from message metadata). The list above only refetches on
  // chat switches, so a fresh project's row sat on "Untitled" until a full
  // page reload. Patch the active row instantly, then refetch so a row that
  // wasn't saved yet still shows up — overlaying the live title again in case
  // the server save hasn't persisted the description at fetch time. Only
  // blank descriptions are filled, so a user rename is never stomped.
  const liveDescription = useStore(descriptionAtom);

  useEffect(() => {
    if (!isAuthenticated || !liveDescription || !activeChatId) {
      return;
    }

    setChats((prev) => overlayLiveTitle(prev, activeChatId, liveDescription));
    getAll()
      .then((fresh) => setChats(overlayLiveTitle(fresh, activeChatId, liveDescription)))
      .catch(() => {});
  }, [liveDescription, isAuthenticated, activeChatId]);

  useEffect(() => {
    if (!isAuthenticated) {
      setSubscription(null);
      return;
    }

    apiGetSubscription()
      .then(setSubscription)
      .catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (profileOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.top - 8, left: collapsed ? rect.right + 8 : rect.left });
    }
  }, [profileOpen, collapsed]);

  // Any navigation dismisses the drawer — on a phone the destination is behind it.
  useEffect(() => {
    closeMobileSidebar();
  }, [location.pathname]);

  // Growing past the breakpoint puts the sidebar back in the layout; a drawer
  // flag left set would otherwise re-open it on the next shrink.
  useEffect(() => {
    if (!isDrawer) {
      closeMobileSidebar();
    }
  }, [isDrawer]);

  // Escape closes, and the page behind must not scroll under the open drawer.
  useEffect(() => {
    if (!isDrawer || !drawerOpen) {
      return undefined;
    }

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        closeMobileSidebar();
      }
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);

    // aria-modal drawer: move focus into it so keyboard and screen-reader users land inside.
    drawerRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [isDrawer, drawerOpen]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      const clickedInside =
        profileRef.current?.contains(target) || document.getElementById('profile-dropdown')?.contains(target);

      if (!clickedInside) {
        setProfileOpen(false);
      }
    }

    if (profileOpen) {
      document.addEventListener('mousedown', handleClick);
    }

    return () => document.removeEventListener('mousedown', handleClick);
  }, [profileOpen]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    function onKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;

      if (!mod) {
        return;
      }

      if (e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    }

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [isAuthenticated]);

  async function performDelete(chatItem: ChatHistoryItem) {
    setDeletingId(chatItem.id);

    try {
      await deleteById(chatItem.id);
      setChats((prev) => prev.filter((c) => c.id !== chatItem.id));

      if (activeChatId === chatItem.urlId || activeChatId === chatItem.id) {
        chatStore.setKey('started', false);
        chatStore.setKey('aborted', false);
        chatStore.setKey('activeChat', '');
        descriptionAtom.set(undefined);
        workbenchStore.showWorkbench.set(false);
        navigate('/');
      }
    } catch {
      // ignore
    } finally {
      setDeletingId(null);
    }
  }

  async function handleLogout() {
    capture(ANALYTICS_EVENTS.USER_LOGGED_OUT);
    setProfileOpen(false);

    try {
      await apiLogout();
    } catch {
      // ignore
    }

    clearAuth();
    navigate('/', { replace: true });
  }

  function handleNewChat() {
    closeMobileSidebar();
    chatStore.setKey('started', false);
    chatStore.setKey('aborted', false);
    chatStore.setKey('activeChat', '');
    descriptionAtom.set(undefined);
    workbenchStore.showWorkbench.set(false);
    navigate('/');
  }

  const initial = (user?.displayName || user?.email || '?')[0].toUpperCase();

  const sections = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q ? chats.filter((c) => (c.description || '').toLowerCase().includes(q)) : chats;

    return bucketChats(filtered);
  }, [chats, search]);

  // Plus/Pro (bought here or in the desktop apps) live beside the website tier, which stays 'free'
  const accountPlanTier =
    subscription?.plan?.product === 'code' || subscription?.plan?.product === 'work' ? subscription.plan.tier : null;
  const planLabel = (accountPlanTier || user?.tier || subscription?.tier || 'free').toString();
  const remainingCredits = liveCredits ?? subscription?.creditsRemaining ?? null;
  const creditsLabel = remainingCredits !== null ? formatCredits(remainingCredits) : null;

  const avatar = (size: number, fontSize: number) => (
    <div
      className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent font-semibold text-page"
      style={{ width: size, height: size, fontSize }}
    >
      {user?.avatarUrl ? <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" /> : initial}
    </div>
  );

  const shell = (
    <aside className="flex h-full flex-col overflow-hidden border-r border-hairline bg-surface">
      {collapsed ? (
        <div className="flex h-full flex-col items-center gap-2 py-4">
          <button
            onClick={handleNewChat}
            className="mb-1 cursor-pointer border-0 bg-transparent p-0 leading-none"
            title="Dunea"
          >
            <img src="/dunea-mark.svg" alt="Dunea" width={24} height={24} className="block" />
          </button>
          <RailButton icon="i-ph:sidebar-simple" title="Expand sidebar" onClick={toggleCollapsed} />
          {isAuthenticated && <RailButton icon="i-ph:plus" title="New project" onClick={handleNewChat} accent />}
          <div className="my-1 h-px w-5 bg-hairline" />
          <RailButton icon="i-ph:magnifying-glass" title="Search projects" onClick={() => setCollapsed(false)} />
          <div className="flex-1" />
          {isAuthenticated ? (
            <div className="relative" ref={profileRef}>
              <button
                ref={triggerRef}
                onClick={() => setProfileOpen(!profileOpen)}
                className="cursor-pointer border-0 bg-transparent p-0"
                title={user?.displayName || user?.email || 'Account'}
              >
                {avatar(28, 11)}
              </button>
            </div>
          ) : (
            <RailButton icon="i-ph:sign-in" title="Sign in" onClick={() => $showAuthModal.set(true)} />
          )}
        </div>
      ) : (
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex-shrink-0">
            <div className="relative flex items-center px-5 pb-3 pt-5">
              <button
                onClick={handleNewChat}
                className="flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0"
                title="Dunea"
              >
                <img src="/dunea-mark.svg" alt="" width={24} height={24} className="block" />
                <span className="ui-brand text-[17px] leading-none text-primary">Dunea</span>
              </button>

              <button
                onClick={isDrawer ? closeMobileSidebar : toggleCollapsed}
                className="absolute right-3.5 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted transition-colors duration-200 hover:bg-raised hover:text-primary touch:h-10 touch:w-10"
                title={isDrawer ? 'Close' : 'Collapse sidebar'}
                aria-label={isDrawer ? 'Close projects' : 'Collapse sidebar'}
              >
                <span className={classNames(isDrawer ? 'i-ph:x' : 'i-ph:sidebar-simple', 'text-[15px]')} />
              </button>
            </div>

            {/* Primary actions — New project + search grouped in one row */}
            {isAuthenticated && (
              <div className="flex gap-2 px-2 pb-2 pt-1">
                <button
                  onClick={handleNewChat}
                  title="New project"
                  className="flex h-10 min-w-0 flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-accent/20 bg-raised text-secondary transition-[border-color,color] duration-200 ease-out hover:border-accent/40 hover:text-primary"
                >
                  <span className="i-ph:plus text-[13px] text-accent" />
                  <span className="text-[13px] font-medium tracking-[-0.005em]">New project</span>
                </button>
                <button
                  onClick={() => setSearchOpen((v) => !v)}
                  aria-label="Search projects"
                  title="Search projects  (⌘K)"
                  className={classNames(
                    'flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg border bg-raised transition-[border-color,color] duration-200 ease-out hover:border-subtle hover:text-primary',
                    searchOpen ? 'border-subtle text-primary' : 'border-hairline text-muted',
                  )}
                >
                  <span className="i-ph:magnifying-glass text-[14px]" />
                </button>
              </div>
            )}

            {/* Search — revealed on demand */}
            {isAuthenticated && (
              <AnimatePresence initial={false}>
                {searchOpen && (
                  <motion.div
                    initial={reduceMotion ? { opacity: 1 } : { height: 0, opacity: 0 }}
                    animate={reduceMotion ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
                    exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                    transition={reduceMotion ? { duration: 0 } : FAST}
                    className="overflow-hidden"
                  >
                    <div className="relative flex items-center px-2.5 pt-3">
                      <span className="i-ph:magnifying-glass pointer-events-none absolute left-[18px] text-[12px] text-muted" />
                      <input
                        value={search}
                        autoFocus
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setSearch('');
                            setSearchOpen(false);
                          }
                        }}
                        placeholder="Search projects…"
                        className="ui-input h-[30px] pl-7 pr-12 text-[12.5px] touch:h-10 touch:pr-3"
                      />
                      <kbd className="ui-kbd pointer-events-none absolute right-[18px] touch:hidden">esc</kbd>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>

          {/* Project list */}
          <div className="modern-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 pb-4 pt-2">
            {!isAuthenticated ? (
              <div className="px-3 py-12 text-center">
                <p className="font-geist text-[13px] font-medium leading-snug text-secondary">Your workspace awaits</p>
                <button
                  onClick={() => $showAuthModal.set(true)}
                  className="mt-2 cursor-pointer border-0 bg-transparent text-[12.5px] font-medium text-accent"
                >
                  Sign in →
                </button>
              </div>
            ) : chats.length === 0 ? (
              <div className="px-3 py-12 text-center">
                <p className="font-geist text-[13px] font-medium leading-snug text-secondary">No projects yet</p>
                <p className="mt-1.5 text-[12px] leading-relaxed text-muted">Your projects will gather here.</p>
              </div>
            ) : sections.length === 0 ? (
              <div className="px-3 py-12 text-center">
                <p className="text-[12.5px] text-muted">Nothing matches “{search.trim()}”</p>
              </div>
            ) : (
              sections.map((section) => (
                <SectionGroup
                  key={section.key}
                  section={section}
                  collapsed={collapsedSections.has(section.key)}
                  onToggle={() => toggleSection(section.key)}
                  reduceMotion={reduceMotion}
                >
                  {section.items.map((item) => {
                    const path = item.urlId || item.id;
                    const isActive = activeChatId === path;

                    return (
                      <ProjectRow
                        key={item.id}
                        item={item}
                        isActive={isActive}
                        deletingId={deletingId}
                        onOpen={() => navigate(`/chat/${path}`)}
                        onDelete={() => setChatToDelete(item)}
                      />
                    );
                  })}
                </SectionGroup>
              ))
            )}

            {/* Admin-only: generations handed off via claim links (read-only trace). */}
            <HandedOffSection />
          </div>

          {/* Footer — account, separated by the default hairline divider */}
          <div className="flex-shrink-0 border-t border-hairline px-2 py-2.5">
            {isAuthenticated ? (
              <div className="relative" ref={profileRef}>
                <button
                  ref={triggerRef}
                  onClick={() => setProfileOpen(!profileOpen)}
                  className={classNames(
                    'group flex w-full cursor-pointer items-center gap-2.5 rounded-lg border px-2 py-2 text-left transition-[background-color,border-color] duration-200 ease-out',
                    profileOpen
                      ? 'border-hairline bg-raised'
                      : 'border-transparent hover:border-hairline hover:bg-raised',
                  )}
                >
                  {avatar(26, 11)}
                  <div className="min-w-0 flex-1">
                    <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] font-medium leading-tight tracking-[-0.005em] text-primary">
                      {user?.displayName || user?.email || 'Account'}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <span className="ui-caption text-accent">{planLabel}</span>
                      {creditsLabel && (
                        <>
                          <span className="h-0.5 w-0.5 rounded-full bg-faint" />
                          <span className="text-[11px] tabular-nums leading-none text-muted">
                            {creditsLabel} credits
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <span
                    className={classNames(
                      'i-ph:caret-up-down text-[12px] text-muted transition-opacity duration-200',
                      profileOpen ? 'opacity-100' : 'opacity-70',
                    )}
                  />
                </button>
              </div>
            ) : (
              <button
                onClick={() => $showAuthModal.set(true)}
                className="h-10 w-full cursor-pointer rounded-lg border border-subtle bg-surface text-[12.5px] font-medium text-secondary transition-[border-color,color] duration-200 ease-out hover:border-prominent hover:text-primary"
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      )}
    </aside>
  );

  const overlays = (
    <>
      {/* Profile dropdown */}
      {createPortal(
        <AnimatePresence>
          {profileOpen && dropdownPos && (
            <div
              className="ui-root fixed z-[9999] -translate-y-full"
              style={{ top: dropdownPos.top, left: Math.max(8, Math.min(dropdownPos.left, window.innerWidth - 288)) }}
            >
              {/* Opens upward from the trigger — cap it to the space above so a landscape phone can scroll it. */}
              <motion.div
                id="profile-dropdown"
                initial={{ opacity: 0, y: 4, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={FAST}
                className="w-[280px] overflow-y-auto overflow-x-hidden overscroll-contain rounded-[14px] border border-subtle bg-surface shadow-[0_28px_68px_-20px_rgba(0,0,0,0.75)]"
                style={{ maxHeight: Math.max(160, dropdownPos.top - 8) }}
              >
                <div className="px-3.5 pb-3 pt-3.5">
                  <div className="flex items-center gap-3">
                    {avatar(38, 14)}
                    <div className="min-w-0 flex-1">
                      <p className="m-0 overflow-hidden text-ellipsis whitespace-nowrap text-[13.5px] font-medium tracking-[-0.01em] text-primary">
                        {user?.displayName || 'User'}
                      </p>
                      <p className="mb-0 mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap text-[11px] text-muted">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                </div>

                {creditsLabel && (
                  <div className="mx-2.5 mb-2.5 rounded-lg border border-hairline bg-raised px-3.5 py-3">
                    <div className="flex items-end justify-between">
                      <div className="flex flex-col gap-1">
                        <span className="ui-caption">Credits left</span>
                        <span className="ui-serif text-[22px] leading-none tabular-nums text-primary">
                          {creditsLabel}
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setProfileOpen(false);
                          openAccountSettings('plans', 'sidebar');
                        }}
                        className="cursor-pointer border-0 bg-transparent p-0 pb-0.5 text-[11px] font-medium tracking-[-0.005em] text-accent transition-colors duration-200 ease-out hover:text-primary"
                      >
                        Manage →
                      </button>
                    </div>
                  </div>
                )}

                <div className="ui-caption px-3.5 pb-1 pt-0.5">Workspace</div>

                <div className="px-1.5 pb-1.5">
                  <DropdownItem
                    icon="i-ph:globe-hemisphere-west"
                    label="Domains"
                    onClick={() => {
                      setProfileOpen(false);
                      navigate('/domains');
                    }}
                  />
                  <DropdownItem
                    icon="i-ph:hand-coins"
                    label="Affiliates"
                    onClick={() => {
                      setProfileOpen(false);
                      navigate('/affiliates');
                    }}
                  />
                  <DropdownItem
                    icon="i-ph:gear-six"
                    label="Settings"
                    onClick={() => {
                      setProfileOpen(false);
                      openAccountSettings('profile');
                    }}
                  />
                </div>

                <div className="ui-caption border-t border-hairline px-3.5 pb-1 pt-1.5">Resources</div>

                <div className="px-1.5 pb-1.5">
                  <DropdownLink
                    icon="i-ph:map-trifold"
                    label="Roadmap"
                    href="https://dunea.ai/roadmap"
                    onClick={() => setProfileOpen(false)}
                  />
                  <DropdownLink
                    icon="i-ph:discord-logo"
                    label="Community"
                    href="https://discord.gg/mCdjW3Ztv8"
                    onClick={() => setProfileOpen(false)}
                  />
                </div>

                <div className="border-t border-hairline px-1.5 pb-1.5 pt-1">
                  <DropdownItem icon="i-ph:sign-out" label="Sign out" danger onClick={handleLogout} />
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body,
      )}

      <DeleteProjectDialog
        open={chatToDelete !== null}
        projectName={chatToDelete?.description ?? ''}
        busy={deletingId !== null && chatToDelete !== null && deletingId === chatToDelete.id}
        onCancel={() => setChatToDelete(null)}
        onConfirm={() => {
          if (chatToDelete) {
            performDelete(chatToDelete);
          }

          setChatToDelete(null);
        }}
      />
    </>
  );

  /*
   * Drawer mode. The sidebar is portaled to <body> and fixed, so it never
   * occupies layout width — the chat and composer get the whole viewport, which
   * is the entire point on a phone. Rendered only while open so nothing
   * off-screen can steal a tap.
   */
  if (isDrawer) {
    return (
      <>
        {createPortal(
          <AnimatePresence>
            {drawerOpen && (
              <div className="ui-root fixed inset-0 z-[60] lg:hidden">
                <motion.div
                  className="absolute inset-0 bg-black/60"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={reduceMotion ? { duration: 0 } : FAST}
                  onClick={closeMobileSidebar}
                />
                <motion.div
                  ref={drawerRef}
                  role="dialog"
                  aria-label="Projects"
                  aria-modal="true"
                  tabIndex={-1}
                  className="absolute inset-y-0 left-0 w-[min(86vw,300px)] bg-surface pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pt-[env(safe-area-inset-top)] shadow-[0_0_60px_-10px_rgba(0,0,0,0.8)] outline-none"
                  initial={reduceMotion ? { opacity: 0 } : { x: '-100%' }}
                  animate={reduceMotion ? { opacity: 1 } : { x: 0 }}
                  exit={reduceMotion ? { opacity: 0 } : { x: '-100%' }}
                  transition={reduceMotion ? { duration: 0 } : { duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                >
                  {shell}
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body,
        )}
        {overlays}
      </>
    );
  }

  return (
    <motion.div
      className="ui-root flex-shrink-0"
      animate={{ width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH }}
      transition={reduceMotion ? { duration: 0 } : FAST}
      style={{ width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH }}
    >
      {shell}
      {overlays}
    </motion.div>
  );
}

function DropdownItem({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={classNames(
        'group flex w-full cursor-pointer items-center gap-2.5 rounded-md border-0 bg-transparent px-2.5 py-[7px] text-left text-[12.5px] tracking-[-0.005em] transition-colors duration-200 hover:bg-raised touch:py-2.5',
        danger ? 'text-danger' : 'text-secondary',
      )}
    >
      <span
        className={classNames(icon, 'text-[14px] opacity-70 transition-opacity duration-100 group-hover:opacity-100')}
      />
      <span className="flex-1">{label}</span>
    </button>
  );
}

function DropdownLink({
  icon,
  label,
  href,
  onClick,
}: {
  icon: string;
  label: string;
  href: string;
  onClick: () => void;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="group flex w-full cursor-pointer items-center gap-2.5 rounded-md border-0 bg-transparent px-2.5 py-[7px] text-left text-[12.5px] tracking-[-0.005em] text-secondary no-underline transition-colors duration-200 hover:bg-raised touch:py-2.5"
    >
      <span
        className={classNames(icon, 'text-[14px] opacity-70 transition-opacity duration-100 group-hover:opacity-100')}
      />
      <span className="flex-1">{label}</span>
      <span className="i-ph:arrow-up-right text-[11px] text-faint opacity-60" />
    </a>
  );
}

/* ─────────────────────────── themed delete dialog ─────────────────────────── */

function DeleteProjectDialog({
  open,
  projectName,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  projectName: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) {
    return null;
  }

  return (
    <AccessibleModal
      title="Delete project"
      description={
        projectName
          ? `${projectName} and all of its history will be permanently removed. This can't be undone.`
          : "This project and all of its history will be permanently removed. This can't be undone."
      }
      maxWidth={440}
      dismissible={!busy}
      onClose={onCancel}
    >
      <div className="flex items-start gap-3.5">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] border border-danger/20 bg-danger/10">
          <span className="i-ph:trash text-[17px] text-danger" />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <h3 className="ui-serif m-0 mb-1.5 text-[19px] leading-tight tracking-[-0.01em] text-primary">
            Delete this project?
          </h3>
          <p className="m-0 text-[12.5px] leading-[1.55] text-muted">
            {projectName ? (
              <>
                <span className="text-secondary">{projectName}</span> and all of its history will be permanently
                removed. This can't be undone.
              </>
            ) : (
              <>This project and all of its history will be permanently removed. This can't be undone.</>
            )}
          </p>
        </div>
      </div>

      <div className="mt-5 flex justify-end gap-2 border-t border-hairline pt-3.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="cursor-pointer rounded-lg border border-hairline bg-transparent px-3.5 py-2 text-[12.5px] font-medium tracking-[-0.005em] text-secondary transition-colors duration-200 hover:bg-raised hover:text-primary disabled:cursor-default disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-danger/30 bg-danger/10 px-4 py-2 text-[12.5px] font-medium tracking-[-0.005em] text-danger transition-colors duration-100 hover:bg-danger/20 disabled:cursor-default"
        >
          {busy && <span className="i-ph:spinner-gap animate-spin text-[13px]" />}
          {busy ? 'Deleting…' : 'Delete project'}
        </button>
      </div>
    </AccessibleModal>
  );
}
